import { formatPublicSourceLabel } from "@/features/machines/addressing";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import { formatWidgetRelative } from "./format";
import { WidgetFooter, WidgetShell } from "./WidgetShell";

interface AvailabilityWidgetProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  /** Unidade da fonte física; a unidade percentual do resultado não é a da regra. */
  sourceUnit?: string | null;
  editable?: boolean;
  onConfigure?: () => void;
  /**
   * Gera o grafico DESTE card, que e a linha do tempo rodando x parado
   * (`utilization`) — nao a serie do sinal ancora.
   */
  onGenerateChart?: () => void;
}

const WINDOW_LABELS: Record<
  NonNullable<DashboardWidget["settings"]["availability"]>["window"],
  string
> = {
  current: "agora",
  "6h": "6h",
  "12h": "12h",
  "24h": "24h",
  "48h": "48h",
  "7d": "7 dias",
  "30d": "30 dias",
};

export function AvailabilityWidget({
  widget,
  value,
  sourceUnit,
  editable,
  onConfigure,
  onGenerateChart,
}: AvailabilityWidgetProps) {
  const config = widget.settings.availability ?? {
    mode: "state",
    window: "current",
    activeWhen: widget.settings.activeWhen ?? 1,
  };
  const isAvailableNow = resolveAvailabilityNow(widget, value);
  const hasHistoricalWindow = config.window !== "current";
  const isWaitingAccumulator = value.accumulatorDelta?.status === "warming";
  const windowLabel = WINDOW_LABELS[config.window];
  const percentage = isAvailableNow === null ? null : isAvailableNow ? 100 : 0;
  const tone = isAvailableNow === false ? "danger" : isAvailableNow === true ? "ok" : "warn";
  const statusLabel =
    isAvailableNow === null
      ? isWaitingAccumulator
        ? "AGUARDANDO"
        : "SEM SINAL"
      : isAvailableNow
        ? "DISPONIVEL"
        : "INDISPONIVEL";

  return (
    <WidgetShell
      title={widget.title}
      subtitle={formatPublicSourceLabel(widget.source, widget.settings.signalKind)}
      tone={tone}
      bodyLayout="col"
      size={widget.layout.size}
      alarmEnabled={widget.settings.alarmEnabled}
      editable={editable}
      onConfigure={onConfigure}
      onGenerateChart={onGenerateChart}
      footer={
        <WidgetFooter
          ts={formatWidgetRelative(value.measuredAt)}
          measuredAt={value.measuredAt}
          source={value.source}
          status={isAvailableNow === null ? "muted" : isAvailableNow ? "ok" : "danger"}
          statusLabel={statusLabel}
          animated={isAvailableNow === true}
        />
      }
    >
      <div className="availability">
        <div className="availability__value">
          {percentage === null ? "--" : `${percentage}%`}
        </div>
        <div className="availability__bar" aria-hidden="true">
          <span style={{ width: `${percentage ?? 0}%` }} />
        </div>
        <div className="availability__meta">
          <span>{describeRule(config, widget, sourceUnit)}</span>
          <strong>{hasHistoricalWindow ? "agora" : windowLabel}</strong>
        </div>
        {hasHistoricalWindow ? (
          <div className="availability__hint">janela {windowLabel} salva; exibindo agora</div>
        ) : value.accumulatorDelta ? (
          <div className="availability__hint">base: acumulado</div>
        ) : null}
      </div>
    </WidgetShell>
  );
}

function resolveAvailabilityNow(
  widget: DashboardWidget,
  value: ResolvedWidgetValue,
): boolean | null {
  if (!value.isAvailable || value.numericValue === null) return null;
  const config = widget.settings.availability;

  if (config?.mode === "activity") {
    const threshold = config.minActiveValue ?? 0;
    return Math.abs(value.numericValue) > threshold;
  }

  const activeWhen = config?.activeWhen ?? widget.settings.activeWhen ?? 1;
  if (value.numericValue === activeWhen) return true;
  if (value.numericValue === (activeWhen === 1 ? 0 : 1)) return false;
  return Boolean(value.numericValue);
}

function describeRule(
  config: NonNullable<DashboardWidget["settings"]["availability"]>,
  widget: DashboardWidget,
  sourceUnit: string | null | undefined,
): string {
  if (config.mode === "activity") {
    const threshold = config.minActiveValue ?? 0;
    const unit = resolveActivityUnit(widget.source, sourceUnit);
    return unit ? `avanço > ${threshold} ${unit}` : `avanço > ${threshold}`;
  }
  return `estado = ${config.activeWhen ?? 1}`;
}

/**
 * H5.3 — unidade que acompanha o threshold de atividade.
 * Em `_v`, é a unidade publicada (ex: L/min). Em `_acc`, o threshold se
 * aplica ao delta entre amostras (unidade/leitura). Para outros casos
 * (calc, fallback), omite a unidade para não inventar.
 */
function resolveActivityUnit(source: string, sourceUnit: string | null | undefined): string | null {
  // Não reutilizar settings.unit: '%' pertence ao resultado derivado, e não
  // prova a unidade do limiar. Uma fonte que declara '%' continua válida.
  const baseUnit = sourceUnit?.trim();
  const tag = /^G\d+_P\d+_(s|acc|v)$/i.exec(source);
  const signal = tag?.[1] as "s" | "acc" | "v" | undefined;
  if (signal === "v" && baseUnit) return baseUnit;
  if (signal === "acc") return baseUnit ? `${baseUnit}/leitura` : "unidade/leitura";
  return null;
}
